/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

interface IColorObject {
	r: number;
	g: number;
	b: number;
	a: number;
}

/**
 * [Create a color object, access the rgba properties directly, or via css ready rgba/ hex strings]
 * @param  {number & colorObject} r [Accepts red value or a colorObject]
 * @param  {number}    g [green]
 * @param  {number}    b [blue]
 * @param  {number}    a [alpha]
 * @return {[Color]}      [A color.]
 */
export class Color {
	public readonly r: number;
	public readonly g: number;
	public readonly b: number;
	public readonly a: number;

	public rgba: string;
	public hex: string | undefined;
	public constructor(r: number | IColorObject, g?: number, b?: number, a?: number) {
		if (typeof r === 'number') {
			//more than one
			this.r = r;
			this.g = g!;
			this.b = b!;
			this.a = a!;
		} else if (typeof r === 'object') {
			this.r = r.r;
			this.g = r.g;
			this.b = r.b;
			this.a = r.a;
		} else {
			throw new Error('Colors must be well constructed');
		}

		this.rgba = `rgba(${this.r},${this.g},${this.b},${this.a})`;
		if (this.a === 1) {
			this.hex = '#' + this.r.toString(16) + this.g.toString(16) + this.b.toString(16);
		}
	}
}

export class GoldenColors {
	public static ratio: number = 0.618033988749895;
	private _defaultAlpha: number;

	public constructor(defaultAlpha: number = 1) {
		this._defaultAlpha = defaultAlpha;
	}

	public static HSVtoRGB(
		inputH: number | { h: number; s: number; v: number },
		s?: number,
		v?: number
	): { r: number; g: number; b: number } {
		let h: number, r!: number, g!: number, b!: number;
		if (arguments.length === 1) {
			const hsv: { h: number; s: number; v: number } = inputH as { h: number; s: number; v: number };
			s = hsv.s;
			v = hsv.v;
			h = hsv.h;
		} else {
			h = inputH as number;
		}

		const i = Math.floor(h * 6);
		const f = h * 6 - i;
		const p = v! * (1 - s!);
		const q = v! * (1 - f * s!);
		const t = v! * (1 - (1 - f) * s!);
		switch (i % 6) {
			case 0:
				r = v!;
				g = t;
				b = p;
				break;
			case 1:
				r = q;
				g = v!;
				b = p;
				break;
			case 2:
				r = p;
				g = v!;
				b = t;
				break;
			case 3:
				r = p;
				g = q;
				b = v!;
				break;
			case 4:
				r = t;
				g = p;
				b = v!;
				break;
			case 5:
				r = v!;
				g = p;
				b = q;
				break;
		}
		return {
			r: Math.round(r! * 255),
			g: Math.round(g! * 255),
			b: Math.round(b! * 255),
		};
	}

	public getRandomColor(alpha?: number): Color {
		let hue = Math.random();
		hue += GoldenColors.ratio;
		// hue % 1; //this was here, but does nothing?
		const raw = GoldenColors.HSVtoRGB(hue, 0.5, 0.95);
		return new Color(raw.r, raw.g, raw.b, alpha || this._defaultAlpha);
	}
}
